import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import {jwtDecode} from 'jwt-decode'; // Make sure this is the correct import
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { usePermissions } from '../../context/PermissionContext';
import Loader from '../General/Loader'; // Adjust the import path as needed
import BackButton from '../../components/BackButton';

const SetAllDiv = () => {
  const [currentPercentages, setCurrentPercentages] = useState(null);
  const [newPercentages, setNewPercentages] = useState({
    customer: '',
    customeWelfare: '',
    headOffice: '',
    referals: '',
    stateAdmin: '',
    MSB: '',
  });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');
  const { can } = usePermissions();
  const canEdit = can('master.commissionPercent.edit');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        jwtDecode(token);
        fetchCurrentPercentages();
      } catch (error) {
        console.error('Error decoding token:', error);
        setSnackSeverity('error');
        setSnackMessage('Authentication error. Please log in again.');
        setSnackOpen(true);
        setLoading(false);
      }
    } else {
      setSnackSeverity('error');
      setSnackMessage('Authentication required. Please log in.');
      setSnackOpen(true);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentPercentages = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const decodedToken = jwtDecode(token);
      const userId = decodedToken.userId;

      const response = await axios.get(`${API_BASE_URL}/divisions/percentages/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setCurrentPercentages(response.data);


    } catch (error) {
      console.error('Error fetching percentages:', error);
      setSnackSeverity('error');
      setSnackMessage('Failed to fetch current percentages.');
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPercentages((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleUpdateClick = () => {
    if (!canEdit) return;
    // Validate that the sum is exactly 100
    const total =
      parseFloat(newPercentages.customer || 0) +
      parseFloat(newPercentages.customeWelfare || 0) +
      parseFloat(newPercentages.headOffice || 0) +
      parseFloat(newPercentages.stateAdmin || 0) +
      parseFloat(newPercentages.referals || 0) +
      parseFloat(newPercentages.MSB || 0);

    if (total !== 100) {
      setSnackSeverity('error');
      setSnackMessage('The sum of percentages must be exactly 100%.');
      setSnackOpen(true);
      return;
    }

    setDialogOpen(true);
  };

  const handleConfirmUpdate = async () => {
    if (!canEdit) return;
    setDialogOpen(false);
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const decodedToken = jwtDecode(token);
      const userId = decodedToken.userId;

      const payload = {
        customer: parseFloat(newPercentages.customer),
        customeWelfare: parseFloat(newPercentages.customeWelfare),
        headOffice: parseFloat(newPercentages.headOffice),
        stateAdmin: parseFloat(newPercentages.stateAdmin),
        referals: parseFloat(newPercentages.referals),
        MSB: parseFloat(newPercentages.MSB),
      };

      const response = await axios.post(
        `${API_BASE_URL}/divisions/percentages/${userId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSnackSeverity('success');
      setSnackMessage(response.data.message || 'Percentages updated successfully.');
      setSnackOpen(true);
      setNewPercentages({
        customer: '',
        customeWelfare: '',
        headOffice: '',
        referals: '',
        stateAdmin: '',
        MSB: '',
      });
      fetchCurrentPercentages();
    } catch (error) {
      console.error('Error updating percentages:', error);
      setSnackSeverity('error');
      const msg = (error.response && error.response.data && error.response.data.message) ? error.response.data.message : 'Failed to update percentages.';
      setSnackMessage(msg);
      setSnackOpen(true);
      setLoading(false);
    }
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
    {/* Place BackButton outside the Grid */}
    <BackButton />
    <br/>  <br/>
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 0}}>
      <Card sx={{ p: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Merriweather, serif' }}>
            Set Commission %
          </Typography>
          {currentPercentages ? (
            <React.Fragment>
              <Typography variant="h6" gutterBottom>
                Current Percentages:
              </Typography>
              <Typography variant="body1">
                CUSTOMER %: {currentPercentages.customer}%
              </Typography>
              <Typography variant="body1">
                CUSTOMER WELFARE %: {currentPercentages.customeWelfare}%
              </Typography>
              <Typography variant="body1">
                CUSTOMER REFERRAL %: {currentPercentages.referals}%
              </Typography>
              <Typography variant="body1">
                COMPANY %: {currentPercentages.headOffice}%
              </Typography>
              <Typography variant="body1">
                STATE HEAD %: {currentPercentages.stateAdmin}%
              </Typography>
              <Typography variant="body1">
                Q-PAY CENTRE %: {currentPercentages.MSB}%
              </Typography>

              {canEdit && (
                <>
                  <TextField
                    label="CUSTOMER %"
                    name="customer"
                    type="number"
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    value={newPercentages.customer}
                    onChange={handleInputChange}
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                  />
                  <TextField
                    label="CUSTOMER WELFARE %"
                    name="customeWelfare"
                    type="number"
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    value={newPercentages.customeWelfare}
                    onChange={handleInputChange}
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                  />
                  <TextField
                    label="CUSTOMER REFERRAL %"
                    name="referals"
                    type="number"
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    value={newPercentages.referals}
                    onChange={handleInputChange}
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                  />
                  <TextField
                    label="COMPANY %"
                    name="headOffice"
                    type="number"
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    value={newPercentages.headOffice}
                    onChange={handleInputChange}
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                  />
                  <TextField
                    label="STATE HEAD %"
                    name="stateAdmin"
                    type="number"
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    value={newPercentages.stateAdmin}
                    onChange={handleInputChange}
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                  />
                  <TextField
                    label="Q-PAY CENTRE %"
                    name="MSB"
                    type="number"
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    value={newPercentages.MSB}
                    onChange={handleInputChange}
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateClick}
                    sx={{ mt: 2 }}
                  >
                    Update Percentages
                  </Button>
                </>
              )}
            </React.Fragment>
          ) : (
            <Typography variant="body1" color="textSecondary">
              No percentages set.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update the percentages to:
          </Typography>
          <Typography>
            CUSTOMER %: {newPercentages.customer}%
          </Typography>
          <Typography>
            CUSTOMER WELFARE %: {newPercentages.customeWelfare}%
          </Typography>
          <Typography>
            CUSTOMER REFERRAL %: {newPercentages.referals}%
          </Typography>
          <Typography>
            COMPANY %: {newPercentages.headOffice}%
          </Typography>
          <Typography>
            STATE HEAD %: {newPercentages.stateAdmin}%
          </Typography>
          <Typography>
            Q-PAY CENTRE %: {newPercentages.MSB}%
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmUpdate} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
    </Box>
  );
};

export default SetAllDiv;
